import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { getMultipleStorageValues } from 'App/Helpers';

import { isWebPushAvailable } from '../helpers';

/**
 * Measures handle time after the native prompt resolves to `granted` and
 * reports the bucketed duration to Sentry. `showLoader` is started by the
 * phase click handler, not by this timing.
 *
 * @param {string} storageKey - Base storage namespace for push values.
 * @returns {{
 *   showLoader: boolean,
 *   setShowLoader: (value: boolean) => void,
 *   markStart: () => void,
 *   markEndAndSend: () => void,
 * }}
 */
export const usePushSubscriptionHandleTime = (storageKey) => {
    const [showLoader, setShowLoader] = useState(false);

    const startTimeRef = useRef(null);

    const markStart = useCallback(() => {
        if (startTimeRef.current === null)
            startTimeRef.current = performance.now();
    }, []);

    const markEndAndSend = useCallback(() => {
        if (startTimeRef.current === null) return;
        const seconds = (performance.now() - startTimeRef.current) / 1000;

        let bucket = 'VerySlow';
        if (seconds < 3) bucket = 'Fast';
        else if (seconds < 6) bucket = 'Medium';
        else if (seconds < 9) bucket = 'Slow';

        const {
            ['push.brwPromptCount']: brw_prompt_count,
            ['push.phaseViews']: phase_views,
        } = getMultipleStorageValues(storageKey, [
            'push.brwPromptCount',
            'push.phaseViews',
        ]);

        Sentry.captureEvent({
            message: 'Push Subscription Handle Time',
            level: 'info',
            tags: {
                bucket,
                phase_views,
                brw_prompt_count,
                hash: window.location.hash || 'non',
            },
            contexts: { timing: { value: seconds, unit: 'second' } },
        });

        startTimeRef.current = null;
    }, [storageKey]);

    useEffect(() => {
        let alive = true; // stop updates after unmount
        let status = null; // will hold PermissionStatus

        const sync = (state) => {
            if (!alive) return;
            if (state === 'granted') {
                setShowLoader(true);
                markStart();
            }
        };

        if (typeof Notification !== 'undefined') {
            sync(Notification.permission);
        }

        // Safari and every iOS browser reject this query. Skip it there so the
        // rejection is not reported as Onesignal: PermissionQuery.
        if (
            isWebPushAvailable() &&
            typeof navigator.permissions?.query === 'function'
        ) {
            navigator.permissions
                .query({ name: 'notifications' })
                .then((s) => {
                    if (!alive) return;
                    status = s;
                    sync(s.state);
                    s.onchange = () => sync(s.state);
                })
                .catch(() => {});
        }

        return () => {
            alive = false;
            if (status) status.onchange = null;
        };
    }, [markStart]);

    return { showLoader, setShowLoader, markStart, markEndAndSend };
};
